import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  CircularProgress,
  Chip,
  TextField,
  InputAdornment,
  Pagination,
  Avatar,
  Stack,
  Alert,
  Select,
  MenuItem,
  FormControl,
  Link,
} from '@mui/material';
import {
  Search as SearchIcon,
  Person as PersonIcon,
  SmartToy as BotIcon,
  Chat as ChatIcon,
  AccessTime as TimeIcon,
  CalendarToday as CalendarIcon,
  TrendingUp as TrendingUpIcon,
  Today as TodayIcon,
  DateRange as DateRangeIcon,
  Event as EventIcon,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import apiService from '../services/api';

interface ConversationSummary {
  session_id: string;
  first_message: string;
  last_message: string;
  message_count: number;
  first_timestamp: string;
  last_timestamp: string;
}

interface ConversationMessage {
  id: string;
  query_text: string;
  response_text: string | null;
  timestamp: string;
  response_time_ms: number | null;
  sources_count: number;
}

interface ConversationDetail {
  session_id: string;
  messages: ConversationMessage[];
  total_messages: number;
  first_timestamp: string | null;
  last_timestamp: string | null;
}

interface StatsData {
  today: { sessions: number; messages: number };
  week: { sessions: number; messages: number };
  month: { sessions: number; messages: number };
  year: { sessions: number; messages: number };
}

const ConversationsPage: React.FC = () => {
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<ConversationDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [daysFilter, setDaysFilter] = useState(30);
  const [stats, setStats] = useState<StatsData | null>(null);
  const [loadingStats, setLoadingStats] = useState(true);

  useEffect(() => {
    loadConversations();
  }, [page, daysFilter]);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    setLoadingStats(true);
    try {
      const response = await apiService.get('/api/v1/conversations/stats/dashboard');
      setStats(response.data);
    } catch (err: any) {
      console.error('Failed to load stats:', err);
    } finally {
      setLoadingStats(false);
    }
  };

  const loadConversations = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.get('/api/v1/conversations/', {
        page,
        per_page: 20,
        days: daysFilter,
      });
      setConversations(response.data.conversations);
      setTotalPages(response.data.total_pages);
      setTotal(response.data.total);
    } catch (err: any) {
      setError(err.message || 'Failed to load conversations');
    } finally {
      setLoading(false);
    }
  };

  const loadConversationDetail = async (sessionId: string) => {
    setLoadingDetail(true);
    try {
      const response = await apiService.get(`/api/v1/conversations/${sessionId}`);
      setSelectedConversation(response.data);
    } catch (err: any) {
      setError(err.message || 'Failed to load conversation');
    } finally {
      setLoadingDetail(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatShortDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const filteredConversations = conversations.filter(
    (conv) =>
      conv.first_message.toLowerCase().includes(searchTerm.toLowerCase()) ||
      conv.session_id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box sx={{ height: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column' }}>
      {/* Compact Header Bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1.5,
          pb: 1.5,
          borderBottom: '1px solid',
          borderColor: 'grey.200',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1E293B' }}>
          Conversations
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <Chip
            size="small"
            icon={<ChatIcon sx={{ fontSize: '16px !important' }} />}
            label={`${total} Sessions`}
            color="primary"
            variant="outlined"
            sx={{ fontWeight: 500 }}
          />
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <Select
              value={daysFilter}
              onChange={(e) => {
                setDaysFilter(e.target.value as number);
                setPage(1);
              }}
              sx={{ 
                height: 32,
                fontSize: '0.85rem',
                '& .MuiSelect-select': { py: 0.5 }
              }}
            >
              <MenuItem value={7}>Last 7 days</MenuItem>
              <MenuItem value={14}>Last 14 days</MenuItem>
              <MenuItem value={30}>Last 30 days</MenuItem>
              <MenuItem value={90}>Last 90 days</MenuItem>
              <MenuItem value={365}>Last year</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Box>

      {/* Stats Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        {[
          { label: 'Today', icon: <TodayIcon />, data: stats?.today, color: '#00ACDD' },
          { label: 'This Week', icon: <DateRangeIcon />, data: stats?.week, color: '#10B981' },
          { label: 'This Month', icon: <EventIcon />, data: stats?.month, color: '#8B5CF6' },
          { label: 'This Year', icon: <TrendingUpIcon />, data: stats?.year, color: '#F59E0B' },
        ].map((item) => (
          <Paper
            key={item.label}
            elevation={0}
            sx={{
              p: 2,
              flex: 1,
              border: '1px solid',
              borderColor: 'grey.200',
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: `${item.color}15`,
                color: item.color,
              }}
            >
              {item.icon}
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {item.label}
              </Typography>
              {loadingStats ? (
                <CircularProgress size={16} />
              ) : (
                <>
                  <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                    {item.data?.sessions ?? 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {item.data?.messages ?? 0} msgs
                  </Typography>
                </>
              )}
            </Box>
          </Paper>
        ))}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Main Content */}
      <Box sx={{ display: 'flex', flex: 1, gap: 2, overflow: 'hidden' }}>
        {/* Conversations List */}
        <Paper
          elevation={0}
          sx={{
            width: 380,
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid',
            borderColor: 'grey.200',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          {/* Search */}
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'grey.200' }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search conversations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'grey.400' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* List */}
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : filteredConversations.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">No conversations found</Typography>
              </Box>
            ) : (
              <List disablePadding>
                {filteredConversations.map((conv, index) => (
                  <React.Fragment key={conv.session_id}>
                    <ListItem disablePadding>
                      <ListItemButton
                        selected={selectedConversation?.session_id === conv.session_id}
                        onClick={() => loadConversationDetail(conv.session_id)}
                        sx={{
                          py: 2,
                          px: 2,
                          '&.Mui-selected': {
                            backgroundColor: 'primary.50',
                            borderLeft: '3px solid',
                            borderColor: 'primary.main',
                          },
                        }}
                      >
                        <ListItemText
                          primary={
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {conv.first_message || 'No message'}
                            </Typography>
                          }
                          secondary={
                            <Box sx={{ mt: 0.5 }}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Chip
                                  size="small"
                                  label={`${conv.message_count} msgs`}
                                  sx={{ height: 20, fontSize: '0.7rem' }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                  {formatShortDate(conv.last_timestamp)}
                                </Typography>
                              </Stack>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'grey.500',
                                  display: 'block',
                                  mt: 0.5,
                                  fontSize: '0.65rem',
                                  fontFamily: 'monospace',
                                }}
                              >
                                {conv.session_id.substring(0, 20)}...
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                    {index < filteredConversations.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Box>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'grey.200' }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                size="small"
                sx={{ display: 'flex', justifyContent: 'center' }}
              />
            </Box>
          )}
        </Paper>

        {/* Conversation Detail */}
        <Paper
          elevation={0}
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid',
            borderColor: 'grey.200',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          {loadingDetail ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <CircularProgress />
            </Box>
          ) : selectedConversation ? (
            <>
              {/* Header */}
              <Box
                sx={{
                  p: 2,
                  borderBottom: '1px solid',
                  borderColor: 'grey.200',
                  backgroundColor: 'grey.50',
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Conversation Transcript
                </Typography>
                <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                  <Chip
                    size="small"
                    icon={<ChatIcon />}
                    label={`${selectedConversation.total_messages} messages`}
                  />
                  {selectedConversation.first_timestamp && (
                    <Chip
                      size="small"
                      icon={<CalendarIcon />}
                      label={formatDate(selectedConversation.first_timestamp)}
                      variant="outlined"
                    />
                  )}
                </Stack>
                <Typography
                  variant="caption"
                  sx={{ mt: 1, display: 'block', fontFamily: 'monospace', color: 'grey.500' }}
                >
                  Session ID: {selectedConversation.session_id}
                </Typography>
              </Box>

              {/* Messages */}
              <Box sx={{ flex: 1, overflow: 'auto', p: 3, backgroundColor: 'grey.50' }}>
                <Stack spacing={3}>
                  {selectedConversation.messages.map((msg) => (
                    <Box key={msg.id}>
                      {/* User Message */}
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          mb: 2,
                          alignItems: 'flex-end',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 2,
                            maxWidth: '85%',
                            flexDirection: 'row-reverse',
                          }}
                        >
                          <Avatar
                            sx={{
                              width: 40,
                              height: 40,
                              bgcolor: 'secondary.main',
                              flexShrink: 0,
                            }}
                          >
                            <PersonIcon />
                          </Avatar>
                          <Box
                            sx={{
                              px: 3,
                              py: 2,
                              borderRadius: '16px',
                              backgroundColor: 'primary.main',
                              color: 'primary.contrastText',
                              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                            }}
                          >
                            <Typography variant="body1" sx={{ lineHeight: 1.5 }}>
                              {msg.query_text}
                            </Typography>
                          </Box>
                        </Box>
                        <Typography
                          variant="caption"
                          sx={{ color: 'grey.500', mt: 0.5, mr: 7 }}
                        >
                          {formatDate(msg.timestamp)}
                        </Typography>
                      </Box>

                      {/* AI Response */}
                      {msg.response_text && (
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 2,
                              maxWidth: '85%',
                            }}
                          >
                            <Avatar
                              sx={{
                                width: 40,
                                height: 40,
                                bgcolor: 'primary.main',
                                flexShrink: 0,
                              }}
                            >
                              <BotIcon />
                            </Avatar>
                            <Box
                              sx={{
                                px: 3,
                                py: 2,
                                borderRadius: '16px',
                                backgroundColor: 'background.paper',
                                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                                border: '1px solid',
                                borderColor: 'divider',
                              }}
                            >
                              {/* Response meta info */}
                              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                {msg.sources_count > 0 && (
                                  <Chip
                                    size="small"
                                    label={`${msg.sources_count} sources`}
                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                  />
                                )}
                                {msg.response_time_ms && (
                                  <Chip
                                    size="small"
                                    icon={<TimeIcon sx={{ fontSize: '12px !important' }} />}
                                    label={`${(msg.response_time_ms / 1000).toFixed(1)}s`}
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                  />
                                )}
                              </Stack>
                              <Box
                                sx={{
                                  '& p': { margin: 0, lineHeight: 1.6 },
                                  '& p + p': { mt: 1 },
                                  '& ul, & ol': { pl: 2, mt: 1, mb: 1 },
                                  '& li': { mb: 0.5 },
                                  '& strong': { fontWeight: 600 },
                                  '& code': {
                                    backgroundColor: 'action.hover',
                                    px: 0.5,
                                    py: 0.25,
                                    borderRadius: 0.5,
                                    fontSize: '0.875em',
                                    fontFamily: 'monospace',
                                  },
                                  '& pre': {
                                    backgroundColor: 'action.hover',
                                    p: 1.5,
                                    borderRadius: 1,
                                    overflow: 'auto',
                                    mt: 1,
                                  },
                                  '& table': {
                                    borderCollapse: 'collapse',
                                    width: '100%',
                                    mt: 2,
                                    mb: 2,
                                    fontSize: 'inherit',
                                    fontFamily: 'inherit',
                                  },
                                  '& th, & td': {
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    px: 1.5,
                                    py: 1,
                                    textAlign: 'left',
                                    fontSize: 'inherit',
                                    fontFamily: 'inherit',
                                    lineHeight: 1.6,
                                  },
                                  '& th': {
                                    backgroundColor: 'action.hover',
                                    fontWeight: 600,
                                  },
                                  '& tr:nth-of-type(even)': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                  },
                                  '& a': {
                                    color: 'primary.main',
                                    textDecoration: 'none',
                                    '&:hover': { textDecoration: 'underline' },
                                  },
                                  '& table a': {
                                    color: 'primary.main',
                                    textDecoration: 'none',
                                    '&:hover': { textDecoration: 'underline' },
                                  },
                                  '& blockquote': {
                                    borderLeft: '3px solid',
                                    borderColor: 'grey.300',
                                    margin: '0.5em 0',
                                    paddingLeft: '1em',
                                    color: 'grey.600',
                                  },
                                }}
                              >
                                <ReactMarkdown
                                  remarkPlugins={[remarkGfm]}
                                  components={{
                                    a: ({ href, children }) => (
                                      <Link href={href} target="_blank" rel="noopener noreferrer">
                                        {children}
                                      </Link>
                                    ),
                                  }}
                                >
                                  {msg.response_text}
                                </ReactMarkdown>
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  ))}
                </Stack>
              </Box>
            </>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                flex: 1,
                color: 'grey.500',
              }}
            >
              <ChatIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
              <Typography variant="h6" color="text.secondary">
                Select a conversation
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Choose a session from the list to view the transcript
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default ConversationsPage;
